<template>
  <div>
    <input
      :id="idAttr"
      v-model="selectedValue"
      type="text"
      :name="nameAttr"
      :class="classAttr"
      :placeholder="placeholderAttr"
      :disabled="disableInput"
      :data-cy="slug"
      @focus="onFocusEvent"
      @keyup.enter="close()"
    >
    <div
      v-if="datepicker.show"
      class="picker__mask"
      @click="close"
    />
    <div
      v-if="datepicker.show"
      class="picker__frame"
      :class="{'picker__frame_up': needMoveToUp}"
    >
      <div class="picker__warp">
        <div class="picker__box">
          <div class="picker__header">
            <div class="picker__month">
              <select v-model="datepicker.month">
                <option
                  v-for="(n, i) in datepicker.monthList"
                  :key="i"
                  :value="n"
                >
                  {{ modifiedLocale.months[n-1] }}
                </option>
              </select>
            </div>
            <div class="picker__year">
              <select v-model="datepicker.year">
                <option
                  v-for="(val, i) in datepicker.years"
                  :key="i"
                  :value="val"
                >
                  {{ val }}
                </option>
              </select>
            </div>
            <div
              class="picker__nav--prev"
              @click="prevMonth"
            />
            <div
              class="picker__nav--next"
              @click="nextMonth"
            />
          </div>
          <table class="picker__table">
            <thead>
              <tr>
                <th
                  v-for="(val, i) in modifiedLocale.weekday"
                  :key="i"
                  class="picker__weekday"
                  :class="{
                    picker__weekend:
                      modifiedLocale.startsWeeks == 0 ||
                      modifiedLocale.startsWeeks < 0 ||
                      modifiedLocale.startsWeeks > 6
                        ? i == 0 || i == 6
                        : i == 6 - modifiedLocale.startsWeeks || i == 7 - modifiedLocale.startsWeeks,
                  }"
                >
                  {{ val }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in datepicker.days"
                :key="i"
              >
                <td
                  v-for="(val, j) in row"
                  :key="j"
                  role="presentation"
                >
                  <div
                    class="picker__day"
                    :class="{
                      'picker__day--outfocus': val.month != datepicker.month,
                      'picker__day--infocus': val.month == datepicker.month,
                      'picker__day--today': val.isToday,
                      'picker__day--disabled': val.isDisabled,
                      'picker__day--selected picker__day--highlighted':
                        selectedValue == val.dateString,
                    }"
                    @click="val.isDisabled ? false : select(val.dateString)"
                  >
                    {{ val.day }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="picker__footer">
            <button
              class="picker__button--clear"
              type="button"
              @click="clear"
            >
              {{ modifiedLocale.clearBtn }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, watch } from "vue";

export default defineComponent({
  name: "MyDatepicker",
  props: {
    slug: {
      type: String,
      default: ""
    },
    idAttr: {
      type: String,
      default: ""
    },
    nameAttr: {
      type: String,
      default: ""
    },
    classAttr: {
      type: String,
      default: ""
    },
    valueAttr: {
      type: String,
      default: "",
    },
    placeholderAttr: {
      type: String,
      default: "",
    },
    yearMinus: {
      type: Number,
      default: 0,
    },
    from: {
      type: String,
      default: "",
    },
    to: {
      type: String,
      default: "",
    },
    disabledDate: {
      type: Array,
      default: () => {
        return [];
      },
    },
    locale: {
      type: Object,
      default: () => {
        return {
          format: "YYYY/MM/DD",
          weekday: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
          startsWeeks: 0,
          todayBtn: "Today",
          clearBtn: "Clear",
          closeBtn: "Close",
        };
      },
    },
    disableInput: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["value-changed"],
  setup(props, { emit }) {
    let modifiedLocale = Object.assign({
      format: "YYYY/MM/DD",
      weekday: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      months: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      startsWeeks: 0,
      todayBtn: "Today",
      clearBtn: "Clear",
      closeBtn: "Close",
      slash: ["/", "/"],
      slashOffset: [],
    }, props.locale);
    let formatSetting = reactive({
      format: modifiedLocale.format,
      formatRegexp: new RegExp("([0-9]{4})/([0-9]{2})/([0-9]{2})"),
      yearIndex: 1,
      monthIndex: 3,
      dateIndex: 5,
    });
    let dateRegexp = new RegExp(
        "([Y]{4}|[y]{4}|[M]{2}|[m]{2}|[D]{2}|[d]{2})([^ a-zA-Z])([Y]{4}|[y]{4}|[M]{2}|[m]{2}|[D]{2}|[d]{2})([^ a-zA-Z])([Y]{4}|[y]{4}|[M]{2}|[m]{2}|[D]{2}|[d]{2})"
    );
    let dateFormatGroup = modifiedLocale.format.match(dateRegexp);
    if (!dateFormatGroup) {
      formatSetting.format = "YYYY/MM/DD";
    }
    if (dateFormatGroup) {
      const isY = (v) => v == "YYYY" || v == "yyyy";
      const isM = (v) => v == "MM" || v == "mm";
      const isD = (v) => v == "DD" || v == "dd";
      let tempRegexp = "";
      let slashOffset = 0;
      for (let i = 1; i < dateFormatGroup.length; i++) {
        if (i == 2) {
          tempRegexp += "([^ a-zA-Z])";
          modifiedLocale.slash[0] = dateFormatGroup[i];
          continue;
        }
        if (i == 4) {
          tempRegexp += "([^ a-zA-Z])";
          modifiedLocale.slash[1] = dateFormatGroup[i];
          continue;
        }
        if (isY(dateFormatGroup[i])) {
          tempRegexp += "([0-9]{4})";
          formatSetting.yearIndex = i;
          slashOffset += 5;
          modifiedLocale.slashOffset.push(slashOffset);
          continue;
        }
        if (isM(dateFormatGroup[i])) {
          tempRegexp += "([0-9]{2})";
          formatSetting.monthIndex = i;
          slashOffset += 3;
          modifiedLocale.slashOffset.push(slashOffset);
          continue;
        }
        if (isD(dateFormatGroup[i])) {
          tempRegexp += "([0-9]{2})";
          formatSetting.dateIndex = i;
          slashOffset += 3;
          modifiedLocale.slashOffset.push(slashOffset);
          continue;
        }
      }
      formatSetting.formatRegexp = new RegExp(tempRegexp);
    }

    const formatDate = (dateObj, hasMinus, format) => {
      if (format == undefined) {
        format = formatSetting.format;
      }
      let yyyy = dateObj.getFullYear();
      if (hasMinus) {
        yyyy = parseInt(yyyy) - parseInt(props.yearMinus);
      }
      format = format.replace(/yyyy/g, yyyy);
      format = format.replace(/YYYY/g, yyyy);
      format = format.replace(/MM/g, ("0" + (dateObj.getMonth() + 1)).slice(-2));
      format = format.replace(/dd/g, ("0" + dateObj.getDate()).slice(-2));
      format = format.replace(/DD/g, ("0" + dateObj.getDate()).slice(-2));
      format = format.replace(/HH/g, ("0" + dateObj.getHours()).slice(-2));
      format = format.replace(/mm/g, ("0" + dateObj.getMinutes()).slice(-2));
      format = format.replace(/ss/g, ("0" + dateObj.getSeconds()).slice(-2));
      format = format.replace(/SSS/g, ("00" + dateObj.getMilliseconds()).slice(-3));
      return format;
    };

    const selectedValue = ref("");
    const datepicker = reactive({
      show: false,
      hasRange: computed(() => {
        let result = false;
        if (
            props.from &&
            props.to &&
            formatSetting.formatRegexp.test(props.from) &&
            formatSetting.formatRegexp.test(props.to)
        ) {
          result = true;
        }
        return result;
      }),
      year: 2021,
      years: computed(() => {
        let yearList = [];
        let fromDate = props.from.match(formatSetting.formatRegexp);
        let toDate = props.to.match(formatSetting.formatRegexp);

        if (!datepicker.hasRange) {
          for (let i = datepicker.year - 10; i < datepicker.year + 10; i++) {
            yearList.push(i);
          }
        } else {
          for (let i = fromDate[formatSetting.yearIndex]; i <= toDate[formatSetting.yearIndex]; i++) {
            yearList.push(i);
          }
        }
        return yearList;
      }),
      month: 1,
      monthList: computed(() => {
        let result = [];
        for (let i = 1; i <= 12; i++) {
          if (datepicker.hasRange) {
            let fromDate = props.from.match(formatSetting.formatRegexp);
            let toDate = props.to.match(formatSetting.formatRegexp);

            if (
                fromDate &&
                datepicker.year == parseInt(fromDate[formatSetting.yearIndex]) &&
                i < parseInt(fromDate[formatSetting.monthIndex])
            ) {
              if (datepicker.month <= i) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                datepicker.month = parseInt(fromDate[formatSetting.monthIndex]);
              }
              continue;
            }
            if (
                toDate &&
                datepicker.year == parseInt(toDate[formatSetting.yearIndex]) &&
                i > parseInt(toDate[formatSetting.monthIndex])
            ) {
              if (datepicker.month >= i) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                datepicker.month = parseInt(toDate[formatSetting.monthIndex]);
              }
              continue;
            }
          }
          result.push(i);
        }
        return result;
      }),
      days: computed(() => {
        let year = parseInt(datepicker.year) + parseInt(props.yearMinus);
        let month = datepicker.month;
        let startDate = new Date(year + "/" + month + "/1");
        let lastDate = new Date(year, month, 0);
        let startDateWeekday = startDate.getDay();
        let lastDateWeekday = lastDate.getDay();
        let startsWeeks =
            modifiedLocale.startsWeeks < 0 || modifiedLocale.startsWeeks > 6
                ? 0
                : modifiedLocale.startsWeeks;
        if (startDateWeekday != startsWeeks) {
          startDate.setDate(startDate.getDate() - (startDateWeekday - startsWeeks));
          if (startDateWeekday - startsWeeks < 0) {
            startDate.setDate(startDate.getDate() - 7);
          }
        }
        if (lastDateWeekday != startsWeeks + 6) {
          let padding = startsWeeks + 6 - lastDateWeekday;
          lastDate.setDate(lastDate.getDate() + padding);
        }
        let days = [];
        let row = [];
        let today = formatDate(new Date(), true);
        let isDisabled = false;
        while (startDate.getTime() - lastDate.getTime() <= 0) {
          isDisabled = false;
          let yyyy = parseInt(startDate.getFullYear()) - parseInt(props.yearMinus);
          let mm = startDate.getMonth() + 1;
          let dd = startDate.getDate();
          if (datepicker.hasRange) {
            let fromDate = props.from.match(formatSetting.formatRegexp);
            let toDate = props.to.match(formatSetting.formatRegexp);
            if (
                yyyy < fromDate[formatSetting.yearIndex] ||
                (yyyy == fromDate[formatSetting.yearIndex] &&
                    mm < fromDate[formatSetting.monthIndex]) ||
                (yyyy == fromDate[formatSetting.yearIndex] &&
                    mm == fromDate[formatSetting.monthIndex] &&
                    dd < fromDate[formatSetting.dateIndex])
            ) {
              isDisabled = true;
            }
            if (
                yyyy > toDate[formatSetting.yearIndex] ||
                (yyyy == toDate[formatSetting.yearIndex] &&
                    mm > toDate[formatSetting.monthIndex]) ||
                (yyyy == toDate[formatSetting.yearIndex] &&
                    mm == toDate[formatSetting.monthIndex] &&
                    dd > toDate[formatSetting.dateIndex])
            ) {
              isDisabled = true;
            }
          }
          if (!isDisabled && props.disabledDate.length > 0) {
            let checkKey = props.disabledDate.findIndex((rawDate) => {
              let tmpData = rawDate.match(formatSetting.formatRegexp);
              let tmpYear = tmpData[formatSetting.yearIndex];
              let tmpMonth = tmpData[formatSetting.monthIndex];
              let tmpDate = tmpData[formatSetting.dateIndex];
              let modifiedDate = formatDate(
                  new Date(tmpYear + "/" + tmpMonth + "/" + tmpDate),
                  true
              );
              return modifiedDate == formatDate(startDate, true);
            });
            isDisabled = checkKey >= 0 ? true : false;
          }
          let dateObj = {
            year: yyyy,
            month: mm,
            day: dd,
            weekday: startDate.getDay(),
            dateString: formatDate(startDate, true),
            isToday: formatDate(startDate, true) == today,
            isDisabled: isDisabled,
          };
          row.push(dateObj);
          if (row.length >= 7) {
            days.push(row);
            row = [];
          }
          startDate.setDate(startDate.getDate() + 1);
        }
        return days;
      }),
    });

    if (!datepicker.hasRange) {
      datepicker.year = 2021
    } else {
      let toDate = props.to.match(formatSetting.formatRegexp);
      datepicker.year = toDate[formatSetting.yearIndex]
    }
    watch(selectedValue, (value) => {
      if (value != "") {
        let result = "";
        if (formatSetting.formatRegexp.test(value)) {
          let temp = value.match(formatSetting.formatRegexp);
          result = formatDate(
              new Date(
                  temp[formatSetting.yearIndex],
                  temp[formatSetting.monthIndex] - 1,
                  temp[formatSetting.dateIndex]
              ),
              false
          );
        } else {
          // result = prevValue;
          result = value
        }
        selectedValue.value = result;
      }
      emit("value-changed", value);
    });
    watch(
        () => datepicker.show,
        (state) => {
          if (state) {
            let thisMonth = new Date();
            if (selectedValue.value) {
              let temp = selectedValue.value.match(formatSetting.formatRegexp);
              if (temp) {
                thisMonth = new Date(
                    temp[formatSetting.yearIndex],
                    temp[formatSetting.monthIndex] - 1,
                    temp[formatSetting.dateIndex]
                );
                datepicker.year = thisMonth.getFullYear();
                datepicker.month = thisMonth.getMonth() + 1;
              }
            }
          }
        }
    );
    watch(
        () => props.valueAttr,
        (value, prevValue) => {
          if (value == "") {
            selectedValue.value = "";
            return false;
          }
          if (value != prevValue) {
            let result = "";
            if (formatSetting.formatRegexp.test(value)) {
              let temp = value.match(formatSetting.formatRegexp);
              result = formatDate(
                  new Date(
                      temp[formatSetting.yearIndex],
                      temp[formatSetting.monthIndex] - 1,
                      temp[formatSetting.dateIndex]
                  ),
                  false
              );
              selectedValue.value = result;
            }
          }
        }
    );

    const prevMonth = () => {
      let tempPrevYear = datepicker.month == 1 ? datepicker.year - 1 : datepicker.year;
      let tempPrevMonth = datepicker.month == 1 ? 12 : datepicker.month - 1;
      if (datepicker.hasRange) {
        let fromDate = props.from.match(formatSetting.formatRegexp);
        if (tempPrevYear < fromDate[formatSetting.yearIndex]) {
          return false;
        }
        if (
            tempPrevYear == fromDate[formatSetting.yearIndex] &&
            tempPrevMonth < fromDate[formatSetting.monthIndex]
        ) {
          return false;
        }
      }
      datepicker.year = tempPrevYear;
      datepicker.month = tempPrevMonth;
    };

    const nextMonth = () => {
      let tempNextYear = datepicker.month == 12 ? datepicker.year + 1 : datepicker.year;
      let tempNextMonth = datepicker.month == 12 ? 1 : datepicker.month + 1;
      if (datepicker.hasRange) {
        let toDate = props.to.match(formatSetting.formatRegexp);
        if (tempNextYear > toDate[formatSetting.yearIndex]) {
          return false;
        }
        if (
            tempNextYear == toDate[formatSetting.yearIndex] &&
            tempNextMonth > toDate[formatSetting.monthIndex]
        ) {
          return false;
        }
      }
      datepicker.year = tempNextYear;
      datepicker.month = tempNextMonth;
    };
    const selectToday = () => {
      let today = new Date();
      let tempYear = parseInt(today.getFullYear()) - parseInt(props.yearMinus);
      let tempMonth = today.getMonth() + 1;
      if (datepicker.hasRange) {
        let fromDate = props.from.match(formatSetting.formatRegexp);
        let toDate = props.to.match(formatSetting.formatRegexp);
        if (today < fromDate) {
          tempYear = fromDate[formatSetting.yearIndex];
          tempMonth = fromDate[formatSetting.monthIndex];
          today = fromDate;
        }
        if (today > toDate) {
          tempYear = toDate[formatSetting.yearIndex];
          tempMonth = toDate[formatSetting.monthIndex];
          today = toDate;
        }
      }
      datepicker.year = tempYear;
      datepicker.month = tempMonth;
      selectedValue.value = formatDate(today, true);
      datepicker.show = false;
    };
    const clear = () => {
      selectedValue.value = "";
      datepicker.show = false;
    };
    const close = () => {
      datepicker.show = false;
    };
    const select = (value) => {
      selectedValue.value = value;
      datepicker.show = false;
    };

    const needMoveToUp = ref(false);
    const onFocusEvent = (event) => {
      let potisionY = event.target.getBoundingClientRect().y;
      needMoveToUp.value = false;
      if (window.innerHeight - potisionY <= 290) {
        needMoveToUp.value = true;
      }
      datepicker.show = true;
    }

    select(props.valueAttr);

    return {
      modifiedLocale,
      selectedValue,
      datepicker,
      prevMonth,
      nextMonth,
      selectToday,
      select,
      clear,
      close,
      needMoveToUp,
      onFocusEvent,
    };
  },
});
</script>

<style lang="scss" scoped>
#app {

  .picker__mask {
    width: 100%;
    height: 100%;
    position: fixed;
    background: #ffffff00;
    z-index: 10000;
    top: 0;
    left: 0;
  }

  .picker__frame {
    position: absolute;
    z-index: 10001;
  }

  .picker__frame_up {
    top: -300px;
  }

  .picker__warp {
    background-color: white;
    border-radius: 5px;
    box-shadow: 3px 3px 15px rgba(18, 47, 61, 0.5), -3px -3px 15px rgba(248, 253, 255, 0.9),
    inset 3px 3px 15px transparent, inset -3px -3px 15px transparent;
  }

  .picker__box {
    //padding: 20px;
  }

  .picker__header {
    position: relative;
    padding-top:2rem;
    padding-bottom: 2rem;

    select {
      border: none;
      background-color: white;
      -webkit-appearance: none;
      appearance: none;
      outline: none;
    }
  }

  .picker__footer,
  .picker__header,
  .picker__table {
    text-align: center;
    width: 20rem;
  }

  .picker__table {
    thead, tbody {
      border-bottom: solid 1px #f4f4f4;
    }
  }

  .picker__month,
  .picker__year {
    font-weight: 500;
    font-size: 1.2rem;
    display: inline-block;
    margin-left: 0.25em;
    margin-right: 0.25em;
  }

  .picker__nav--next,
  .picker__nav--prev {
    position: absolute;
    padding: 0.5em 1.25em;
    width: 1em;
    height: 1em;
    box-sizing: content-box;
    top: -0.25em;
    margin-top: 2rem;
  }

  .picker__nav--prev {
    left: 0em;
    padding-right: 1.25em;
  }

  .picker__nav--next {
    right: 0em;
    padding-left: 1.25em;
  }

  @media (min-width: 24.5em) {
    .picker__select--month,
    .picker__select--year {
      margin-top: -0.5em;
    }

    .picker__nav--next,
    .picker__nav--prev {
      top: -0.33em;
    }

    .picker__nav--prev {
      padding-right: 1.5em;
    }

    .picker__nav--next {
      padding-left: 1.5em;
    }
  }

  .picker__nav--next:before,
  .picker__nav--prev:before {
    content: " ";
    border-top: 0.5em solid transparent;
    border-bottom: 0.5em solid transparent;
    border-right: 0.75em solid #000;
    width: 0;
    height: 0;
    display: block;
  }

  .picker__nav--next:before {
    border-right: 0;
    border-left: 0.75em solid #000;
  }

  .picker__nav--next:hover,
  .picker__nav--prev:hover {
    cursor: pointer;
    color: #000;
    background: #b1dcfb;
  }

  .picker--focused .picker__day--highlighted,
  .picker__day--highlighted:hover,
  .picker__day--infocus:hover,
  .picker__day--outfocus:hover {
    color: #000;
    cursor: pointer;
    border-radius: 100%;
    border: solid 1px black;
  }

  .picker__day--disabled:before {
    border-top-color: #aaa;
  }

  .picker__day--outfocus {
    color: #ddd;
  }

  .picker--focused .picker__day--selected,
  .picker__day--selected,
  .picker__day--selected:hover {
    border-radius: 100%;
    background: black;
    color: #fff;
  }

  .picker--focused .picker__day--disabled,
  .picker__day--disabled,
  .picker__day--disabled:hover {
    border-radius: 100%;
    background: white;
    border-color: #f5f5f5;
    color: #ddd;
    cursor: pointer;
  }

  .picker__day--highlighted.picker__day--disabled,
  .picker__day--highlighted.picker__day--disabled:hover {
    background: #bbb;
  }

  .picker__weekday {
    width: 14.285714286%;
    font-size: 1em;
    padding-bottom: 0.25em;
    color: #666;
    font-weight: 500;
  }

  @media (min-height: 33.875em) {
    .picker__weekday {
      padding-bottom: 0.5em;
    }
  }

  .picker__button--clear,
  .picker__button--close,
  .picker__button--today {
    border: 1px solid #fff;
    background: #fff;
    font-size: 0.8em;
    //padding: 0.66em 0;
    font-weight: 700;
    width: 33%;
    display: inline-block;
    vertical-align: bottom;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }

  //.picker__button--clear:hover,
  //.picker__button--close:hover,
  //.picker__button--today:hover {
  //  cursor: pointer;
  //  color: #000;
  //  background: #b1dcfb;
  //  border-bottom-color: #b1dcfb;
  //}
  //
  //.picker__button--clear:focus,
  //.picker__button--close:focus,
  //.picker__button--today:focus {
  //  background: #b1dcfb;
  //  border-color: #0089ec;
  //  outline: 0;
  //}

  //.picker__button--clear:before,
  //.picker__button--close:before,
  //.picker__button--today:before {
  //  position: relative;
  //  display: inline-block;
  //  height: 0;
  //}

  //.picker__button--clear:before,
  //.picker__button--today:before {
  //  content: " ";
  //  margin-right: 0.45em;
  //}
  //
  //.picker__button--today:before {
  //  top: -0.05em;
  //  width: 0;
  //  border-top: 0.66em solid #0059bc;
  //  border-left: 0.66em solid transparent;
  //}

  //.picker__button--clear:before {
  //  top: -0.25em;
  //  width: 0.66em;
  //  //border-top: 3px solid #e20;
  //}

  .picker__button--clear {
    background-position: center;
    background-image: url("data:image/svg+xml,%3Csvg id='bin-20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cg id='icons'%3E%3Cpath d='M14.5 6.4V17h-9V6.4h9m1.5-.9c0-.3-.2-.5-.5-.5h-11c-.3 0-.5.2-.5.5V18c0 .3.2.5.5.5h11c.3 0 .5-.2.5-.5V5.5zM13 2c-.3 0-.5-.2-.5-.5S12.3 1 12 1H8c-.3 0-.5.2-.5.5S7.3 2 7 2H4c-.3 0-.5.2-.5.5V3c0 .3.2.5.5.5h12c.3 0 .5-.2.5-.5v-.5c0-.3-.2-.5-.5-.5h-3z'%3E%3C/path%3E%3C/g%3E%3C/svg%3E%0A");    //background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10'><linearGradient id='gradient'><stop offset='10%' stop-color='%23F00'/><stop offset='90%' stop-color='%23fcc'/> </linearGradient><rect fill='url(%23gradient)' x='0' y='0' width='100%' height='100%'/></svg>");
    background-repeat: no-repeat;
  }

  .picker__button--close:before {
    content: "\D7";
    top: -0.1em;
    vertical-align: top;
    font-size: 1.1em;
    margin-right: 0.35em;
    color: #777;
  }

  .picker__button--today[disabled],
  .picker__button--today[disabled]:hover {
    background: #f5f5f5;
    border-color: #f5f5f5;
    color: #ddd;
    cursor: default;
  }

  .picker__button--today[disabled]:before {
    border-top-color: #aaa;
  }

  .picker__day {
    margin: 0.2rem;
    padding: 0.35rem;
    width: 2.2rem;
    height: 2.2rem;
    border: solid 1px transparent;
  }

  .picker__day--today {
    //position: relative;
  }

  .picker__day--today:before {
    //content: " ";
    //position: absolute;
    //top: 2px;
    //right: 2px;
    //width: 0;
    //height: 0;
    //border-top: 0.5em solid #0059bc;
    //border-left: 0.5em solid transparent;
  }

  .picker__weekend {
    //color: #e20;
  }
}

</style>
